import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiRequest, ApiError } from './config'
import { createApiUrl } from './config'
import type { Podcast } from '../types/podcast'

export const PODCAST_QUERY_KEYS = {
  all: ['podcasts'] as const,
  detail: (id: string) => ['podcast', id] as const,
} as const

/**
 * Fetch all podcasts for the current user
 */
export const usePodcasts = (): UseQueryResult<Podcast[], ApiError> => {
  return useQuery({
    queryKey: PODCAST_QUERY_KEYS.all,
    queryFn: async (): Promise<Podcast[]> => {
      return apiRequest<Podcast[]>('/api/podcast')
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const fetchAllPodcastsForCurrentUser = async (): Promise<Podcast[]> => {
  return apiRequest<Podcast[]>('/api/podcast')
}

export const usePodcast = (id: string): UseQueryResult<Podcast, ApiError> => {
  return useQuery({
    queryKey: PODCAST_QUERY_KEYS.detail(id),
    queryFn: async (): Promise<Podcast> => apiRequest<Podcast>(`/api/podcast/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Update a podcast. Note: Language change is blocked.
 */
export const updatePodcast = async (id: string, patch: Partial<Pick<Podcast, 'title' | 'audioFileUrl' | 'hostAVoiceId' | 'hostBVoiceId' | 'speed' | 'volume' | 'emotion' | 'languageIso' | 'style' | 'isPublic'>>): Promise<void> => {
  return apiRequest<void>(`/api/podcast/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

/**
 * Clone an existing podcast into a new language (backend leaves original untouched)
 */
export const clonePodcastToLanguage = async (id: string, payload: { languageIso: string }): Promise<{ originalPodcastId: string; clonedPodcastId: string; languageIso: string; message: string }> => {
  const endpoint = `/api/podcast/${id}/clone`
  const url = createApiUrl(endpoint)
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })
  if (!response.ok && response.status !== 202) {
    let msg = 'Failed to clone podcast'
    try {
      const data = await response.json()
      msg = data.error || data.message || msg
    } catch {
      // Ignore JSON parsing errors
    }
    throw new ApiError(msg, response.status, response.statusText)
  }
  // Some servers may still return 200/202 with JSON payload
  let data: Record<string, unknown> | null = null
  try {
    if (response.headers.get('Content-Type')?.includes('application/json')) {
      data = await response.json()
    }
  } catch {
    // Ignore JSON parsing errors
  }
  if (!data) {
    // Fallback minimal shape (client will refetch via signalR events)
    data = { originalPodcastId: id, clonedPodcastId: 'pending', languageIso: payload.languageIso, message: 'Clone accepted' }
  }
  return data as { originalPodcastId: string; clonedPodcastId: string; languageIso: string; message: string }
}

/**
 * Delete a podcast by ID
 */
export const deletePodcast = async (id: string): Promise<void> => {
  return apiRequest<void>(`/api/podcast/${id}`, {
    method: 'DELETE',
  })
}

// New multi-stage endpoints
export const createFromUrlAndStartScript = async (payload: { url: string; style: string; durationMinutes: number; hostAVoiceId?: string; hostBVoiceId?: string }): Promise<{ id: string }> => {
  return apiRequest<{ id: string }>(`/api/podcast/from-source/url`, { method: 'POST', body: JSON.stringify(payload) })
}
export const createFromPromptAndStartScript = async (payload: { prompt: string; style: string; durationMinutes: number; hostAVoiceId?: string; hostBVoiceId?: string }): Promise<{ id: string }> => {
  return apiRequest<{ id: string }>(`/api/podcast/from-source/prompt`, { method: 'POST', body: JSON.stringify(payload) })
}
export const createFromDocumentUrlAndStartScript = async (payload: { fileUrl: string; style: string; durationMinutes: number; hostAVoiceId?: string; hostBVoiceId?: string }): Promise<{ id: string }> => {
  return apiRequest<{ id: string }>(`/api/podcast/from-source/document-url`, { method: 'POST', body: JSON.stringify(payload) })
}
export const createFromYouTubeAndStartScript = async (payload: { youtubeUrl: string; style: string; durationMinutes: number; languageIso: string; additionalInstructions?: string; hostAVoiceId?: string; hostBVoiceId?: string; isPublic?: boolean }): Promise<{ id: string }> => {
  return apiRequest<{ id: string }>(`/api/podcast/from-source/youtube`, { method: 'POST', body: JSON.stringify(payload) })
}
export const startBulkSynthesisForPodcast = async (id: string): Promise<void> => {
  await apiRequest(`/api/podcast/${id}/synthesize-audio`, { method: 'POST' })
}
export const assembleFinalEpisodeForPodcast = async (id: string): Promise<{ id: string; audioFileUrl: string | null }> => {
  return apiRequest<{ id: string; audioFileUrl: string | null }>(`/api/podcast/${id}/assemble-final`, { method: 'POST' })
}

// Mark that the current user played a podcast (server tracks HasPlayedAudio)
export const markPodcastPlayedForCurrentUser = async (podcastId: string): Promise<void> => {
  return apiRequest<void>(`/api/podcast/${podcastId}/played`, { method: 'POST' })
}

// Start refining the script for an existing podcast
export const refinePodcastScript = async (
  id: string,
  payload: { instruction: string }
): Promise<{ podcastId: string; revisionId: string; message: string } | void> => {
  // Endpoint returns 202 Accepted with a minimal ack; client listens to SignalR for progress
  try {
    return await apiRequest<{ podcastId: string; revisionId: string; message: string }>(`/api/podcast/${id}/refine`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  } catch (e) {
    // Surface error to caller
    throw e
  }
}

export const overwritePodcastScript = async (
  podcastId: string,
  body: { lines: Array<{ lineId?: string | null; text: string; host: 'A' | 'B' }> }
): Promise<{ linesChanged: number }> => {
  return apiRequest<{ linesChanged: number }>(`/api/podcast/${podcastId}/lines/bulk`, {
    method: 'PUT',
    body: JSON.stringify(body)
  })
}

export const updatePodcastLine = async (
  podcastId: string,
  lineId: string,
  body: { text?: string; host?: 'A' | 'B'; emotion?: string }
): Promise<void> => {
  await apiRequest(`/api/podcast/${podcastId}/lines/${lineId}`, { method: 'PUT', body: JSON.stringify(body) })
}

// Removed per-line regenerate endpoint and logic

export const deletePodcastLine = async (
  podcastId: string,
  lineId: string
): Promise<void> => {
  await apiRequest(`/api/podcast/${podcastId}/lines/${lineId}`, { method: 'DELETE' })
}

export const createPodcastLineBelow = async (
  podcastId: string,
  afterLineId: string,
  body?: { text?: string; host?: 'A' | 'B' }
): Promise<{ id: string } & Record<string, unknown>> => {
  return apiRequest(`/api/podcast/${podcastId}/lines`, {
    method: 'POST',
    body: JSON.stringify({ afterLineId: afterLineId, text: body?.text, host: body?.host })
  })
}

// Removed editor undo/redo/session endpoints
